import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import moment from "moment";
import { sendSubmissionFeedbackApi } from "./submissionStorage";
import { formatMetricValue } from "./submissionPerformance";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const MAX_MESSAGE_LENGTH = 2000;
const MAX_PROGRESS_METRICS = 2;

const CHANNELS = {
  chat: { label: "Chat", sent: "Chat message sent", failed: "Chat message not sent" },
  email: { label: "Email", sent: "Email sent", failed: "Email not sent" },
};

const getFirstName = (submission = {}) => {
  if (submission.firstName) return String(submission.firstName).trim();
  const name = String(submission.memberName || "").trim();
  if (!name || name === "Unknown" || name.includes("@")) return "";
  return name.split(/\s+/)[0];
};

/** "Body Fat" → "body fat", keeps acronyms like "BMI". */
const metricNoun = (label = "") =>
  String(label)
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      word.length > 1 && word === word.toUpperCase() ? word : word.toLowerCase()
    )
    .join(" ") || "progress";

const describeMetric = (metric) => {
  const noun = metricNoun(metric.label);
  const current = formatMetricValue(metric.current, metric.unit);
  const baseline = formatMetricValue(metric.baseline, metric.unit);
  const isFirstReading =
    metric.points.length < 2 || metric.points[0]?.isCurrent === true;

  if (isFirstReading) {
    return `Your ${noun} is at ${current} on this check-in — a great starting point.`;
  }
  if (metric.improved === "flat" || baseline === current) {
    return `Your ${noun} is holding steady at ${current} since you started.`;
  }
  const change = `${metric.delta < 0 ? "down" : "up"} ${formatMetricValue(
    Math.abs(metric.delta),
    metric.unit
  )}`;
  const outro =
    metric.improved === "up"
      ? `${change}, well done!`
      : `${change}. Let's keep working on it together.`;
  return `Your ${noun} has gone from ${baseline} to ${current} since you started — ${outro}`;
};

/**
 * Progress sentence(s) from buildSubmissionPerformance() metrics
 * (baseline → this check-in). Target values are goals, not progress.
 */
export const buildProgressMessage = (metrics = []) => {
  const usable = (metrics || []).filter(
    (m) =>
      m &&
      Number.isFinite(m.current) &&
      m.points?.length &&
      !/target/i.test(m.label || "")
  );
  if (!usable.length) return "";
  const trending = usable.filter((m) => m.points.length >= 2);
  return (trending.length ? trending : usable)
    .slice(0, MAX_PROGRESS_METRICS)
    .map(describeMetric)
    .join(" ");
};

const entryChannels = (entry = {}) => {
  const results = entry.results || {};
  const keys = new Set(Array.isArray(entry.channels) ? entry.channels : []);
  Object.entries(results).forEach(([key, outcome]) => {
    if (outcome && (outcome.sent || outcome.error)) keys.add(key);
  });
  return Array.from(keys)
    .filter((key) => CHANNELS[key])
    .map((key) => ({
      key,
      failed: results[key]?.sent === false,
      error: results[key]?.error || "",
    }));
};

const ChannelOption = ({ label, checked, disabled, hint, onChange }) => (
  <Box>
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label={<Typography sx={{ fontSize: 13.5 }}>{label}</Typography>}
    />
    {hint ? (
      <Typography sx={{ fontSize: 12, color: MUTED, ml: 4, mt: -0.75 }}>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

/**
 * Coach → member review feedback (chat message and/or email) + sent history.
 */
export default function CoachFeedbackCard({ submission, metrics, onSent }) {
  const { enqueueSnackbar } = useSnackbar();
  const chatAvailable = Boolean(submission?.memberId);
  const emailAvailable = Boolean(submission?.email);
  const [message, setMessage] = useState("");
  const [chatChecked, setChatChecked] = useState(true);
  const [emailChecked, setEmailChecked] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setMessage("");
    setChatChecked(true);
    setEmailChecked(true);
  }, [submission?.id]);

  const firstName = getFirstName(submission || {});
  const progressText = useMemo(() => buildProgressMessage(metrics), [metrics]);
  const history = useMemo(
    () =>
      [...(submission?.coachFeedback || [])].sort(
        (a, b) => new Date(b?.sentAt || 0) - new Date(a?.sentAt || 0)
      ),
    [submission?.coachFeedback]
  );

  const wantChat = chatAvailable && chatChecked;
  const wantEmail = emailAvailable && emailChecked;
  const canSend = Boolean(message.trim()) && (wantChat || wantEmail) && !sending;

  const handleInsertProgress = () => {
    if (!progressText) return;
    setMessage((prev) => {
      const existing = prev.trimEnd();
      const next = existing
        ? `${existing} ${progressText}`
        : `Hi ${firstName || "there"}, ${progressText
            .charAt(0)
            .toLowerCase()}${progressText.slice(1)}`;
      return next.slice(0, MAX_MESSAGE_LENGTH);
    });
  };

  const notifyResults = (result) => {
    if (!result.results) {
      enqueueSnackbar(result.message || "Feedback sent", { variant: "success" });
      return;
    }
    [
      ["chat", wantChat],
      ["email", wantEmail],
    ].forEach(([key, requested]) => {
      if (!requested) return;
      const outcome = result.results[key];
      if (outcome?.sent) {
        enqueueSnackbar(CHANNELS[key].sent, { variant: "success" });
        return;
      }
      enqueueSnackbar(
        outcome?.error
          ? `${CHANNELS[key].failed}: ${outcome.error}`
          : CHANNELS[key].failed,
        { variant: "warning" }
      );
    });
  };

  const handleSend = async () => {
    if (!canSend || !submission?.id) return;
    setSending(true);
    const result = await sendSubmissionFeedbackApi(submission.id, {
      message: message.trim(),
      sendChat: wantChat,
      sendEmail: wantEmail,
    });
    setSending(false);
    if (!result.ok) {
      enqueueSnackbar(result.message || "Couldn't send the feedback", {
        variant: "error",
      });
      return;
    }
    notifyResults(result);
    setMessage("");
    if (onSent) await onSent(result.submission);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        bgcolor: "#fff",
        mb: 2.5,
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
          Message to member
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: 13, color: MUTED }}>
          Send review feedback as a chat message, an email, or both.
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <TextField
          fullWidth
          multiline
          minRows={4}
          value={message}
          disabled={sending}
          placeholder={`Write feedback for ${firstName || "this member"}…`}
          onChange={(e) =>
            setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))
          }
          inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
          helperText={`${message.length}/${MAX_MESSAGE_LENGTH}`}
          FormHelperTextProps={{ sx: { textAlign: "right", mr: 0 } }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            mt: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <ChannelOption
              label="Send chat message"
              checked={wantChat}
              disabled={!chatAvailable || sending}
              hint={chatAvailable ? "" : "No portal account for chat"}
              onChange={setChatChecked}
            />
            <ChannelOption
              label="Send email"
              checked={wantEmail}
              disabled={!emailAvailable || sending}
              hint={emailAvailable ? "" : "No email on file"}
              onChange={setEmailChecked}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
            <Tooltip title={progressText ? "" : "No measurements yet"}>
              <span>
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  disabled={!progressText || sending}
                  onClick={handleInsertProgress}
                >
                  Insert progress
                </button>
              </span>
            </Tooltip>
            <button
              type="button"
              className="small-contained-button"
              disabled={!canSend}
              onClick={handleSend}
            >
              {sending ? (
                <Box
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
                >
                  <CircularProgress size={14} color="inherit" />
                  Sending…
                </Box>
              ) : (
                "Send"
              )}
            </button>
          </Box>
        </Box>

        {history.length > 0 ? (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${BORDER}` }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                color: MUTED,
                textTransform: "uppercase",
                mb: 1.25,
              }}
            >
              Feedback sent
            </Typography>
            <Box sx={{ display: "grid", gap: 1 }}>
              {history.map((entry, index) => (
                <Box
                  key={`${entry?.sentAt || ""}-${index}`}
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    border: `1px solid ${BORDER}`,
                    bgcolor: "#f8fafc",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 0.75,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}
                    >
                      {entry?.sentAt
                        ? moment(entry.sentAt).format("DD MMM YYYY h:mm A")
                        : "—"}
                    </Typography>
                    {entry?.sentByName ? (
                      <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                        · {entry.sentByName}
                      </Typography>
                    ) : null}
                    {entryChannels(entry).map((channel) => (
                      <Tooltip
                        key={channel.key}
                        title={
                          channel.failed
                            ? channel.error || CHANNELS[channel.key].failed
                            : ""
                        }
                      >
                        <Chip
                          size="small"
                          label={CHANNELS[channel.key].label}
                          color={channel.failed ? "warning" : "default"}
                          variant={channel.failed ? "filled" : "outlined"}
                          sx={{ height: 22, fontSize: 11.5, fontWeight: 600 }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      color: "#0f172a",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {entry?.message || ""}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
